import { ref, computed } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import api from '@/services/api'

// Extended here rather than relying on plugins/dayjs.js having been imported
// first — dayjs is a singleton and extend() is idempotent.
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

const TZ = 'Australia/Sydney'

// A vehicle's registration is flagged once it is this many days (or fewer) from
// expiring. Already-expired vehicles are always flagged.
export const REGO_WARNING_DAYS = 30

// Vehicles fetched per alert check. Well above the current fleet size.
const VEHICLE_PAGE_LIMIT = 200

// Module-scoped state: every component that calls useVehicleAlerts() shares the
// same list, so the header bell and the dashboard popup agree and we only hit
// /vehicles once per page load.
const alerts = ref([])
const loading = ref(false)
const loaded = ref(false)
let inFlight = null

/**
 * Whole days between today (Sydney) and a YYYY-MM-DD expiry date.
 * Negative = already expired. Returns null for missing/unparseable dates.
 */
export function regoExpiryDays(dateString) {
  if (!dateString) return null
  const raw = String(dateString).slice(0, 10)
  // Validate before dayjs.tz(): the timezone plugin throws RangeError on an
  // unparseable value rather than returning an invalid instance, and this runs
  // during render — an exception here would blank the whole page.
  if (!dayjs(raw, 'YYYY-MM-DD', true).isValid()) return null
  const expiry = dayjs.tz(raw, TZ).startOf('day')
  if (!expiry.isValid()) return null
  return expiry.diff(dayjs().tz(TZ).startOf('day'), 'day')
}

/** 'expired' | 'expiring' | 'ok' | null */
export function regoExpiryStatus(dateString) {
  const days = regoExpiryDays(dateString)
  if (days === null) return null
  if (days < 0) return 'expired'
  if (days <= REGO_WARNING_DAYS) return 'expiring'
  return 'ok'
}

/** Human-readable countdown, e.g. "Expires in 9 days" / "Expired 3 days ago". */
export function regoExpiryLabel(dateString) {
  const days = regoExpiryDays(dateString)
  if (days === null) return ''
  if (days < 0) {
    const n = Math.abs(days)
    return `Expired ${n} ${n === 1 ? 'day' : 'days'} ago`
  }
  if (days === 0) return 'Expires today'
  if (days === 1) return 'Expires tomorrow'
  return `Expires in ${days} days`
}

function buildAlerts(vehicles) {
  // get-vehicles LEFT JOINs technician assignments, so one vehicle can come
  // back on more than one row. De-duplicate by id or the badge count inflates
  // and the v-for lists get duplicate keys.
  const seen = new Set()
  return vehicles
    .filter(v => {
      if (!v || !v.id || seen.has(v.id)) return false
      seen.add(v.id)
      return true
    })
    .map(v => ({
      id: v.id,
      vehicleName: v.vehicle_name,
      registrationNumber: v.registration_number,
      registrationExpiry: v.registration_expiry,
      days: regoExpiryDays(v.registration_expiry),
      status: regoExpiryStatus(v.registration_expiry),
      label: regoExpiryLabel(v.registration_expiry)
    }))
    .filter(a => a.status === 'expired' || a.status === 'expiring')
    .sort((a, b) => a.days - b.days)
}

/**
 * Load every vehicle and recompute the rego-expiry alert list.
 * Call with { force: true } after a vehicle is created or updated so the bell
 * reflects the change without a page reload.
 */
export async function fetchVehicleAlerts({ force = false } = {}) {
  // Share a single request: the header bell and the dashboard both ask for this
  // on mount, and the dashboard must not resolve early against an empty list.
  // A forced call must not join an in-flight request that was issued before the
  // save it is meant to pick up — queue a fresh one behind it instead.
  if (inFlight) {
    if (!force) return inFlight
    return inFlight.then(() => fetchVehicleAlerts({ force: true }))
  }
  if (loaded.value && !force) return alerts.value

  loading.value = true
  inFlight = (async () => {
    try {
      const response = await api.get('/vehicles', { params: { limit: VEHICLE_PAGE_LIMIT, offset: 0 } })
      const list = response.data?.data || []
      alerts.value = buildAlerts(Array.isArray(list) ? list : [])
      loaded.value = true

      // Single page only. The fleet is far below this today, but say so out loud
      // rather than silently never alerting on vehicles past the first page.
      const total = response.data?.pagination?.total
      if (typeof total === 'number' && total > VEHICLE_PAGE_LIMIT) {
        console.warn(
          `Vehicle rego alerts checked the first ${VEHICLE_PAGE_LIMIT} of ${total} vehicles; ` +
          'the remainder were not checked.'
        )
      }
    } catch (error) {
      console.error('Failed to load vehicle rego alerts', error)
      alerts.value = []
    } finally {
      loading.value = false
      inFlight = null
    }
    return alerts.value
  })()

  return inFlight
}

export function useVehicleAlerts() {
  const hasAlerts = computed(() => alerts.value.length > 0)

  const expiredCount = computed(() => alerts.value.filter(a => a.status === 'expired').length)

  return {
    alerts,
    loading,
    loaded,
    hasAlerts,
    expiredCount,
    fetchVehicleAlerts,
    regoExpiryDays,
    regoExpiryStatus,
    regoExpiryLabel,
    REGO_WARNING_DAYS
  }
}
